import React, { PureComponent } from 'react'
import { connect } from 'react-redux'
import { withStyles } from '@material-ui/core/styles'
import Card from '@material-ui/core/Card'
import Legend from '../components/Legend'
import { getLegendsToRender } from '../selectors/legendSelector'
import { RED, YELLOW, GREEN } from '../config/colors'
import { POLAR_VIEW } from '../config/services'
import { FLOOD_RIVER_COLORS, FLOOD_HAZARD_COLORS } from '../layers/paints'
import { withErrorBoundary } from '../errors/ErrorBoundary'

const floodHazardLegend = [
  { label: '0.5m', color: FLOOD_HAZARD_COLORS[0] },
  { label: '1m', color: FLOOD_HAZARD_COLORS[1] },
  { label: '1.5m', color: FLOOD_HAZARD_COLORS[2] },
  { label: '2m', color: FLOOD_HAZARD_COLORS[3] },
  { label: '2.5m', color: FLOOD_HAZARD_COLORS[4] },
  { label: '3m', color: FLOOD_HAZARD_COLORS[5] },
  { label: '3.5m', color: FLOOD_HAZARD_COLORS[6] },
  { label: '4m', color: FLOOD_HAZARD_COLORS[7] },
  { label: '>4m', color: FLOOD_HAZARD_COLORS[8] }
]

const floodRiverClassificationLegend = [
  { label: 'Open/Water', color: FLOOD_RIVER_COLORS[0] },
  { label: 'Smooth', color: FLOOD_RIVER_COLORS[1] },
  { label: 'Rough', color: FLOOD_RIVER_COLORS[2] }
]

const smartIceLegend = [
  { label: 'Smooth', color: 'green' },
  { label: 'Rough', color: 'yellow' },
  { label: 'Mobile', color: 'red' }
]

const polarViewLegend = [
  { label: 'Go', color: 'green' },
  { label: 'Slow go', color: 'yellow' },
  { label: 'No go', color: 'red' }
]

const smartIceQamutikLegend = [
  { label: '<1"', color: 'darkOrange' },
  { label: '2"', color: 'orange' },
  { label: '3"', color: 'wheat' },
  { label: '5"', color: 'powderBlue' },
  { label: '7"', color: 'slateBlue' }
]

const riverIceLegend = [
  { label: 'Open/Water', color: '#009ECE' },
  { label: 'Smooth', color: '#F7D708' },
  { label: 'Rough', color: '#CE0000' }
]

const cmemsSeaIceLegend = [
  { label: '0%', color: 'blue' },
  { label: '20%', color: 'deepSkyBlue' },
  { label: '50%', color: 'greenYellow' },
  { label: '70%', color: 'orangeRed' },
  { label: '100%', color: 'darkRed' }
]

const cmemsSnowThicknessLegend = [
  { label: '0"', color: 'blue' },
  { label: '0.5"', color: 'deepSkyBlue' },
  { label: '1"', color: 'greenYellow' },
  { label: '1.5"', color: 'orangeRed' },
  { label: '2"', color: 'darkRed' }
]

const deformationLegend = [
  { label: '5cm', color: '#00956f' },
  { label: '0cm', color: '#81dc2e' },
  { label: '-5cm', color: '#f6d628' },
  { label: '-10cm', color: '#e98e19' },
  { label: '-15cm', color: '#d11918' }
]

const mopraLegend = [
  { label: '-1.0', color: '#d7191c' },
  { label: '-0.5', color: '#feb86c' },
  { label: '0.0', color: '#cbf882' },
  { label: '0.5 ', color: '#74ce53' },
  { label: '1.0', color: '#1a9641' }
]

const mopraWellsitesLegend = [
  { label: 'good', color: GREEN },
  { label: 'moderate', color: YELLOW },
  { label: 'poor', color: RED }
]

const mopraNdviLegend = [
  { label: '-1.0', color: '#d7191c' },
  { label: '-0.5', color: '#feb86c' },
  { label: '0.0', color: '#cbf882' },
  { label: '0.5', color: '#74ce53' },
  { label: '1.0', color: '#1a9641' }
]

const waterLakeTssLegend = [
  { label: '> 0', color: '#005ea5' },
  { label: '10', color: '#1bd533' },
  { label: '20', color: '#aade2d' },
  { label: '30', color: '#efb123' },
  { label: '40', color: '#b2670a' },
  { label: '50', color: '#660800' },
  { label: 'cloud', color: '#858585' }
]

const layers = [
  {
    name: 'SmartIce',
    label: 'Ice travel',
    opacity: 'raster-opacity',
    datetimeId: 'SmartIce',
    legend: {
      type: 'categorical',
      data: POLAR_VIEW ? polarViewLegend : smartIceLegend
    }
  },
  {
    name: 'RiverIce',
    label: 'River Ice',
    opacity: 'raster-opacity',
    datetimeId: 'RiverIce',
    legend: {
      type: 'categorical',
      data: riverIceLegend
    }
  },
  {
    name: 'FloodHazard',
    label: 'Flood hazard',
    opacity: 'fill-opacity',
    legend: {
      type: 'categorical',
      data: floodHazardLegend,
      dx: 28
    }
  },
  {
    name: 'FloodRiverClassification',
    label: 'Ice classification',
    opacity: 'fill-opacity',
    legend: {
      type: 'categorical',
      data: floodRiverClassificationLegend
    }
  },
  {
    name: 'SmartIceQamutik',
    label: 'Qamutik',
    opacity: 'raster-opacity',
    datetimeId: 'SmartIceQamutik',
    legend: {
      type: 'categorical',
      data: smartIceQamutikLegend
    }
  },
  {
    name: 'CmemsSeaIce',
    label: 'Sea Ice',
    opacity: 'raster-opacity',
    datetimeId: 'Sentinel1',
    legend: {
      type: 'categorical',
      data: cmemsSeaIceLegend
    }
  },
  {
    name: 'CmemsSnowThickness',
    label: 'Snow Thickness',
    opacity: 'raster-opacity',
    datetimeId: 'Sentinel1',
    legend: {
      type: 'categorical',
      data: cmemsSnowThicknessLegend
    }
  },
  {
    name: 'Mopra',
    label: 'Reclaimed Wellsites',
    opacity: 'raster-opacity',
    datetimeId: 'Mopra',
    legend: {
      type: 'categorical',
      data: mopraLegend
    }
  },
  {
    name: 'MopraWellsites',
    label: 'Wellsite health',
    opacity: 'raster-opacity',
    datetimeId: 'Mopra',
    legend: {
      type: 'categorical',
      data: mopraWellsitesLegend
    }
  },
  {
    name: 'WellsiteNdviLease',
    label: 'Reclaimed Wellsites',
    opacity: 'raster-opacity',
    datetimeId: 'Mopra',
    legend: {
      type: 'categorical',
      data: mopraLegend
    }
  },
  {
    name: 'LandDeformationInsar',
    label: 'Land Deformation',
    opacity: 'raster-opacity',
    datetimeId: 'Mopra',
    legend: {
      type: 'categorical',
      data: deformationLegend
    }
  },
  {
    name: 'WaterLakeTss',
    label: 'Lake Tss',
    opacity: 'raster-opacity',
    datetimeId: 'WaterLakeTss',
    legend: {
      type: 'categorical',
      data: waterLakeTssLegend
    }
  }
]

const styles = theme => ({
  card: {
    width: '300px',
    position: 'absolute',
    right: '100px',
    top: '10px',
    zIndex: '3000',
    backgroundColor: 'rgba(255, 255, 255, 0)',
    boxShadow: 'none'
  }
})

class LegendContainer extends PureComponent {
  renderLegends = layers => {
    const legends = layers.map(layer => (
      <Legend layer={layer} key={layer.name} />
    ))

    return legends
  }

  render () {
    return null
    const { classes, legendsToRender } = this.props

    const layersRendered = layers.filter(layer =>
      legendsToRender.includes(layer.name)
    )

    return (
      <Card className={classes.card}>{this.renderLegends(layersRendered)}</Card>
    )
  }
}

const mapStateToProps = state => {
  return {
    legendsToRender: getLegendsToRender(state),
    
  }
}

export default withErrorBoundary(connect(mapStateToProps, null)(
  withStyles(styles)(LegendContainer)
))



// WEBPACK FOOTER //
// ./src/containers/LegendContainer.js